import { isEqual } from "lodash"
import type { NextRouter } from "next/router"
import React, { type ReactNode } from "react"

import { BookingSearch } from "@/lib/bookingSearch"
import { buildBookingSearchResults } from "@/lib/bookingSearch/buildResults"
import { buildBookingSearchCacheKeys } from "@/lib/bookingSearch/cacheKeys/build"
import { BookingSearchContext } from "@/lib/bookingSearch/context"
import { performBookingSearch } from "@/lib/bookingSearch/perform"
import type { Caches } from "@/lib/caches"
import type { Caching } from "@/lib/caching"
import type { ErrorHandling } from "@/lib/error_handling"
import type { Geolocation } from "@/lib/geolocation"
import { Loading } from "@/lib/loading"
import { Modelling } from "@/lib/models"
import type { Notices } from "@/lib/notices"
import { ReactPerformance } from "@/lib/react_performance"
import { debugEnabled } from "@/lib/routing/debugEnabled"
import type { Services } from "@/lib/services"

type BookingSearchContainerProps = {
  caching: Caches.ContextData
  children: ReactNode
  geolocation: Geolocation.ContextData
  loading: Loading.ContextData
  noticeFunctions: Notices.ContextFunctions
  router: NextRouter
  services: Services.ContextData
}

type State = {
  loadingConfig: Loading.Config
  processing: string | undefined
  results: BookingSearch.Results
  update: boolean
}

export class BookingSearchContainerComponent extends React.Component<
  BookingSearchContainerProps,
  State
> {
  public state: State = {
    loadingConfig: {
      dependencies: [`caching`, `geolocation`, `notices`, `services`],
      processId: `bookingSearch`,
    },
    processing: undefined,
    results: buildBookingSearchResults(),
    update: false,
  }

  public componentDidMount = () => {
    this.componentMaybeReady()
  }

  public componentDidUpdate = async (
    prevProps: BookingSearchContainerProps
  ) => {
    const loading = await this.componentMaybeReady()

    if (loading) {
      return
    }

    if (this.cacheKeysChanged(prevProps, this.props)) {
      await this.perform()
    }
  }

  private componentMaybeReady = Loading.curryOnMaybeReady(this, {})

  private cacheKeysChanged = (
    first: BookingSearchContainerProps,
    second: BookingSearchContainerProps
  ): boolean => {
    return !isEqual(this.getCacheKeys(first), this.getCacheKeys(second))
  }

  private getCacheKeys = (
    props: BookingSearchContainerProps
  ): BookingSearch.Cache.Keys | undefined => {
    const keyData = this.getCacheKeyData(props)
    if (!keyData) {
      return
    }

    return buildBookingSearchCacheKeys(keyData)
  }

  private getCacheKeyData = (
    props: BookingSearchContainerProps
  ): BookingSearch.Cache.KeyData | undefined => {
    if (!props.geolocation.position) {
      return
    }

    return {
      distance: props.geolocation.distance,
      position: props.geolocation.position,
      service: props.services.selected,
    }
  }

  private getBusinessValidatorName = (
    props: BookingSearchContainerProps
  ): string => {
    return `Mirror<Business> with ${JSON.stringify(
      this.getCacheKeyData(props)
    )}`
  }

  private onBusinessesValidationFailure: Caching.Functions.OnValidationFailure<
    Caches.Store,
    BookingSearch.Cache.Keys,
    BookingSearch.Cache.Data
  > = ({ cacheArgs, result }) => {
    if (debugEnabled(this.props.router)) {
      const count =
        ((result.subject as BookingSearch.Cache.Data)?.array?.length || 0) -
        (result.salvage?.array?.length || 0)

      const resultTerm = count === 1 ? `result` : `results`

      this.props.noticeFunctions.displayNotice({
        id: `bookingSearchValidationFailure`,
        message: `${count} search ${resultTerm} failed validation; see console for details`,
        type: `error`,
      })

      console.log(`Booking search ${resultTerm} failed validation`, {
        cacheArgs,
        failures: result.failures,
        reasons: result.failures.map((failure) => {
          return failure.reason
        }),
        result,
      })
    }
  }

  private onFetchFailure: ErrorHandling.Functions.OnFatalError = async (
    failure
  ) => {
    if (debugEnabled(this.props.router)) {
      this.props.noticeFunctions.displayNotice({
        id: `bookingSearchFetchFailure`,
        message: `Debug mode: booking search failed, see console for details. Error code: 'bookingSearchFetchFailure'.`,
        type: `error`,
      })

      console.error(
        `Booking search failed. Error code: 'bookingSearchFetchFailure'.`,
        failure
      )
    } else {
      this.props.noticeFunctions.displayNotice({
        id: `bookingSearchFetchFailure`,
        message: `We're sorry, something went wrong when searching for appointments. Please reload the page and try again. If you keep seeing this error, please contact support@wellnow.nz for assistance. Error code: 'bookingSearchFetchFailure'.`,
        type: `error`,
      })
    }
  }

  private perform: BookingSearch.Functions.Perform = async () => {
    const keys = this.getCacheKeys(this.props)

    if (!keys) {
      return buildBookingSearchResults()
    }

    const businesses = await this.props.caching.cache<
      BookingSearch.Cache.Keys,
      BookingSearch.Cache.Data
    >({
      allowSalvage: true,
      caller: `BookingSearchContainer#perform`,
      fetch: async () => {
        this.updateState((state) => {
          return {
            ...state,
            processing: `businesses`,
          }
        })

        const results = await performBookingSearch({
          keys,
          onFailure: this.onFetchFailure,
        })

        return results
      },
      fetchName: `performBookingSearch`,
      keys,
      metadata: {
        expiry: BookingSearch.Cache.expiry,
      },
      name: this.getBusinessValidatorName(this.props),
      onValidationFailure: this.onBusinessesValidationFailure,
      validator: Modelling.validator(`Business`, `mirror`),
    })

    const results = buildBookingSearchResults({ businesses })

    this.updateState((state) => {
      return {
        ...state,
        processing: undefined,
        results,
      }
    })

    return results
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    BookingSearchContainerProps,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (
        Loading.processIsReady(nextProps, `bookingSearch`) &&
        this.cacheKeysChanged(this.props, nextProps)
      ) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  public updateState = ReactPerformance.curryUpdateManagedState(this)

  public render = () => {
    return (
      <BookingSearchContext.Provider
        value={{
          hasAddress: Boolean(this.props.geolocation.address),
          perform: this.perform,
          processing: this.state.processing,
          results: this.state.results,
        }}
      >
        {this.props.children}
      </BookingSearchContext.Provider>
    )
  }
}
